/**
 * Automatisk tilldelning (molnfast-react#1653): instance settings under
 * `global/autoAssignment`, written only by the `autoAssignmentApi` callable.
 * Mirrors the functions' types; schema in workspace docs/schema/platform.md.
 */
import { GuestLinkSummary } from './guestAccess';

export const ISSUE_AUTO_ASSIGNMENT_PATH = 'global/autoAssignment/issues';

/** Every new felanmälan goes to one förvaltare who judges it against the threshold. */
export interface IssueAutoAssignment {
    enabled: boolean;
    /** The förvaltare (external user uid); set when enabled */
    externalUserId?: string;
    /** Whole kr exkl. moms, > 0 */
    threshold: number;
    /** Internal users e-mailed on "över" */
    notifyUids?: Record<string, true>;
    /** Set when turned on or the förvaltare changes; issues created from then on are in scope */
    enabledAt?: string;
    updatedBy: string;
    updatedAt: string;
}

/** The stored node, or null when absent or malformed. */
export const parseIssueAutoAssignment = (raw: unknown): IssueAutoAssignment | null => {
    if (!raw || typeof raw !== 'object') return null;
    const d = raw as Record<string, unknown>;
    if (typeof d.enabled !== 'boolean' || typeof d.threshold !== 'number') {
        console.warn('Invalid global/autoAssignment/issues node');
        return null;
    }
    const notifyUids = d.notifyUids && typeof d.notifyUids === 'object'
        ? Object.fromEntries(Object.keys(d.notifyUids).map((uid) => [uid, true as const]))
        : undefined;
    return {
        enabled: d.enabled,
        threshold: d.threshold,
        ...(typeof d.externalUserId === 'string' ? { externalUserId: d.externalUserId } : {}),
        ...(notifyUids ? { notifyUids } : {}),
        ...(typeof d.enabledAt === 'string' ? { enabledAt: d.enabledAt } : {}),
        updatedBy: typeof d.updatedBy === 'string' ? d.updatedBy : '',
        updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : '',
    };
};

// ============================================================================
// Callable autoAssignmentApi (admin or superadmin)
// ============================================================================

export interface SaveIssueAutoAssignmentPayload {
    enabled: boolean;
    externalUserId?: string;
    threshold: number;
    notifyUids: string[];
}

export type AutoAssignmentApiRequest =
    | { action: 'getIssues' }
    | ({ action: 'saveIssues' } & SaveIssueAutoAssignmentPayload);

/** `link` is the förvaltare's current link, or null when none / no förvaltare. */
export interface IssueAutoAssignmentState {
    setting: IssueAutoAssignment | null;
    link: GuestLinkSummary | null;
}

export type AutoAssignmentApiResponse = { success: true } & IssueAutoAssignmentState;

/** Kr formatted the Swedish way, e.g. "5 000". */
export const formatThreshold = (kr: number): string => kr.toLocaleString('sv-SE');
